import React, { useRef, useState } from 'react';
import { Camera, Loader2, RotateCcw, ScanSearch, Check, Info, Lightbulb, PawPrint, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import api from '../api';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export const BreedIdentifier = ({ onPick, withCatActions = false }) => {
  const { cats, selectedCatId, updateCat } = useApp();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // standalone-only state
  const [facts, setFacts] = useState([]);
  const [factsLoading, setFactsLoading] = useState(false);
  const [saveBreed, setSaveBreed] = useState('');
  const [saveCatId, setSaveCatId] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setB64(await fileToBase64(f));
    setResult(null); setFacts([]); setSavedTo(null);
  };

  const loadFacts = async (breed) => {
    setFactsLoading(true);
    try {
      const r = await api.get('/ai/breed-tips', { params: { breed } });
      setFacts(r.data?.tips || []);
    } catch { setFacts([]); }
    finally { setFactsLoading(false); }
  };

  const identify = async () => {
    if (!b64) { toast.error('Please add a photo first'); return; }
    setLoading(true);
    try {
      const r = await api.post('/ai/breed-id', { image_base64: b64 });
      const res = r.data.result;
      setResult(res);
      if (withCatActions && res?.breeds?.length) {
        const top = res.breeds[0].name;
        setSaveBreed(top);
        setSaveCatId(selectedCatId || cats[0]?.id || '');
        loadFacts(top);
      }
    } catch (e) {
      toast.error('Could not identify the breed. Please try another photo.');
    } finally { setLoading(false); }
  };

  const saveToCat = async () => {
    if (!saveCatId || !saveBreed) return;
    setSaving(true);
    try {
      await updateCat(saveCatId, { breed: saveBreed });
      const c = cats.find((x) => x.id === saveCatId);
      setSavedTo(c?.name || 'your cat');
      toast.success(`Saved ${saveBreed} to ${c?.name || 'your cat'}'s profile`);
    } catch (e) { toast.error('Could not save to profile'); }
    finally { setSaving(false); }
  };

  const reset = () => { setPreview(null); setB64(null); setResult(null); setFacts([]); setSavedTo(null); };

  return (
    <div data-testid="breed-identifier">
      <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="breed-id-file" />

      {!preview ? (
        <button onClick={() => fileRef.current?.click()} data-testid="breed-id-upload" className="flex w-full flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-border bg-secondary/40 py-12 transition hover:border-[hsl(var(--coral))]">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent"><Camera className="h-8 w-8 text-[hsl(var(--coral))]" /></div>
          <p className="font-bold">Tap to add a photo</p>
          <p className="px-6 text-center text-xs text-muted-foreground">A clear, well-lit photo of your cat's face and body works best.</p>
        </button>
      ) : (
        <div className="animate-fade-up overflow-hidden rounded-3xl">
          <img src={preview} alt="cat" className="h-56 w-full object-cover" />
        </div>
      )}

      {preview && !result && (
        <button onClick={identify} disabled={loading} data-testid="breed-id-identify" className="coral-btn mt-4 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-base font-bold text-white disabled:opacity-70">
          {loading ? <><Loader2 className="h-5 w-5 animate-spin" />Identifying…</> : <><ScanSearch className="h-5 w-5" />Identify breed</>}
        </button>
      )}

      {result && (
        <div className="mt-5 animate-fade-up space-y-3" data-testid="breed-id-result">
          {result.summary && <p className="text-sm font-semibold">{result.summary}</p>}
          {(result.breeds || []).length === 0 && (
            <p className="rounded-2xl bg-secondary/60 p-4 text-sm text-muted-foreground">We couldn't confidently match a breed. Try a clearer, closer photo.</p>
          )}
          {(result.breeds || []).map((b, i) => (
            <div key={i} className="rounded-2xl bg-white p-4 card-soft" data-testid={`breed-id-item-${i}`}>
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-extrabold">{b.name}</h3>
                <span className="shrink-0 rounded-full bg-accent px-2.5 py-1 text-xs font-bold text-[hsl(var(--coral))]">{b.confidence}% match</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full bg-[hsl(var(--coral))]" style={{ width: `${b.confidence}%` }} />
              </div>
              {b.why && <p className="mt-2 text-sm text-muted-foreground">{b.why}</p>}
              {onPick && (
                <button onClick={() => onPick(b.name)} data-testid={`breed-id-use-${i}`} className="mt-3 inline-flex items-center gap-1 rounded-full border border-[hsl(var(--coral))] bg-white px-3 py-1.5 text-xs font-bold text-[hsl(var(--coral))]">
                  <Check className="h-3.5 w-3.5" />Use this breed
                </button>
              )}
            </div>
          ))}
          {result.mixed_note && (
            <div className="flex items-start gap-2 rounded-xl bg-accent/60 p-3 text-xs text-accent-foreground">
              <Info className="h-4 w-4 shrink-0" />{result.mixed_note}
            </div>
          )}

          {/* Save breed to a cat's profile (standalone) */}
          {withCatActions && (result.breeds || []).length > 0 && cats.length > 0 && (
            <div className="rounded-2xl bg-white p-4 card-soft" data-testid="breed-id-save">
              {savedTo ? (
                <p className="flex items-center gap-1.5 text-sm font-bold text-green-600" data-testid="breed-id-saved"><Check className="h-4 w-4" />Saved {saveBreed} to {savedTo}'s profile</p>
              ) : (
                <>
                  <p className="font-extrabold">Save this breed to a profile</p>
                  <div className="mt-3 space-y-2">
                    <select value={saveBreed} onChange={(e) => setSaveBreed(e.target.value)} data-testid="breed-id-save-breed" className="h-11 w-full rounded-xl border border-input bg-white px-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
                      {result.breeds.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
                    </select>
                    {cats.length > 1 && (
                      <select value={saveCatId} onChange={(e) => setSaveCatId(e.target.value)} data-testid="breed-id-save-cat" className="h-11 w-full rounded-xl border border-input bg-white px-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-[hsl(var(--coral))]">
                        {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    )}
                    <button onClick={saveToCat} disabled={saving} data-testid="breed-id-save-btn" className="coral-btn flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white disabled:opacity-60">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      Save to {cats.length > 1 ? (cats.find((c) => c.id === saveCatId)?.name || 'profile') : cats[0]?.name}'s profile
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Breed facts (standalone) */}
          {withCatActions && (result.breeds || []).length > 0 && (
            <div className="rounded-2xl bg-white p-4 card-soft" data-testid="breed-id-facts">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent"><Lightbulb className="h-4.5 w-4.5 text-[hsl(var(--coral))]" /></div>
                <h3 className="font-extrabold">Care tips for a {result.breeds[0].name}</h3>
              </div>
              {factsLoading && <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading tips…</div>}
              {!factsLoading && facts.length > 0 && (
                <ul className="mt-3 space-y-3">
                  {facts.map((t, i) => (
                    <li key={i} className="flex gap-3">
                      <PawPrint className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--coral))]" />
                      <div><p className="text-sm font-bold">{t.title}</p><p className="text-sm text-muted-foreground">{t.tip}</p></div>
                    </li>
                  ))}
                </ul>
              )}
              {!factsLoading && facts.length === 0 && <p className="mt-2 text-sm text-muted-foreground">No tips available right now.</p>}
            </div>
          )}

          <button onClick={reset} data-testid="breed-id-reset" className="flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-white py-3 text-sm font-bold"><RotateCcw className="h-4 w-4" />Try another photo</button>
        </div>
      )}
    </div>
  );
};

export default BreedIdentifier;
